/**
 * Lending Configuration API Service
 *
 * Fetches and updates per-tenant lending configuration from the lending-platform
 * service. Uses the legacy ApiClient since the lending-platform-plugins OpenAPI
 * spec does not have typed paths for openapi-fetch.
 *
 * Endpoints consumed (via lending-platform at /api/v1/lending-config):
 *   GET  /api/v1/lending-config          - Get tenant config (404 when none is saved)
 *   PUT  /api/v1/lending-config          - Update tenant config (partial updates)
 *   GET  /api/v1/lending-config/defaults - Get hardcoded default values
 *
 * Type: LendingConfiguration from @aaimplatform/institutional-types/lending-platform-plugins
 *
 * Routing: Vite proxy routes /api/v1/lending-config to lending-platform (port 9360).
 */

import type { components } from '@aaimplatform/institutional-types/lending-platform-plugins';
import { useAuthStore } from '../../stores/auth';
import { CSRF_MUTATING_METHODS, apiErrorMessage, readCsrfCookie } from '../client';

export type LendingConfiguration = components['schemas']['LendingConfiguration'];
type LendingPlatformErrorResponse = components['schemas']['ErrorResponse'];

/**
 * Response shape returned by lending-config GET endpoints.
 * The server wraps config in a { config: ... } envelope.
 */
interface LendingConfigResponse {
  config: LendingConfiguration;
}

/**
 * Lending-platform fetch wrapper. The lending-platform-plugins OpenAPI
 * spec doesn't expose typed paths for openapi-fetch, so this service uses
 * a minimal fetch helper instead of a typed client. Bearer token + tenant
 * scope header injected from the auth store on every request.
 *
 * F-PORTAL-AUDIENCE-SPLIT (2026-05-17): when the page moved to
 * platform-admin, the legacy admin-portal `ApiClient` class did not come
 * along (admin-portal is the only consumer). The fetch helper is inlined
 * at the only consumption site rather than threading a cross-app symbol.
 */
export const APEX_TENANT_ID = '00000000-0000-0000-0000-000000aaaaaa';

/**
 * The tenant GET and PUT /api/v1/lending-config act on: the X-Tenant-ID this
 * service sends. lending-platform takes a platform admin's claimed header as
 * the operative tenant (requireTenantHeader.ts) and reads and writes that one
 * tenant's InstitutionConfig, so the Lending Defaults page edits the signed-in
 * operator's own tenant, or the apex tenant when the session carries none. It
 * sets no default for any other tenant. Null before a session exists.
 */
export function lendingConfigTenantId(user: { tenantId?: string } | null | undefined): string | null {
  if (!user) return null;
  return user.tenantId ?? APEX_TENANT_ID;
}

/**
 * A lending-platform request that did not succeed: the HTTP status and the
 * error code its body carried, beside the message.
 */
export class LendingConfigRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | undefined,
    /** details.missingFields of a LENDING_CONFIGURATION_INCOMPLETE answer. */
    public readonly missingFields: string[] = [],
  ) {
    super(message);
    this.name = 'LendingConfigRequestError';
  }
}

/**
 * True when GET /api/v1/lending-config refused because the tenant has no
 * saved lending configuration (GH #2459): lending-platform answers 404
 * LENDING_CONFIGURATION_MISSING rather than its defaults.
 */
export function isLendingConfigurationMissing(error: unknown): boolean {
  return (
    error instanceof LendingConfigRequestError &&
    error.code === 'LENDING_CONFIGURATION_MISSING'
  );
}

/**
 * True when GET /api/v1/lending-config refused because the tenant's saved
 * configuration lacks a field (503 LENDING_CONFIGURATION_INCOMPLETE). A save
 * that carries every field repairs it.
 */
export function isLendingConfigurationIncomplete(error: unknown): boolean {
  return (
    error instanceof LendingConfigRequestError &&
    error.code === 'LENDING_CONFIGURATION_INCOMPLETE'
  );
}

async function lendingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const state = useAuthStore.getState();
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');
  // browser-token-storage-hardening: auth rides the HttpOnly cookie
  // (credentials:'include'); no Bearer token is read from the store.
  const tenantId = lendingConfigTenantId(state.user);
  if (tenantId) {
    headers.set('X-Tenant-ID', tenantId);
  }
  // CSRF double-submit (N8): echo the csrf-token cookie as X-CSRF-Token on
  // mutating methods, mirroring the openapi-fetch authMiddleware.onRequest.
  const method = (init?.method ?? 'GET').toUpperCase();
  if (CSRF_MUTATING_METHODS.has(method)) {
    const csrf = readCsrfCookie();
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const response = await fetch(`/api${path}`, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    // lending-platform answers the flat ErrorResponse of
    // lending-platform-plugins.openapi.yaml (GH #3936): the code in `error`,
    // missingFields under `details`.
    const errorBody = (await response.json().catch(() => null)) as Partial<LendingPlatformErrorResponse> | null;
    const code = errorBody?.error;
    const missingFields = errorBody?.details?.missingFields;
    throw new LendingConfigRequestError(
      apiErrorMessage(errorBody) ?? `Request failed (${response.status})`,
      response.status,
      typeof code === 'string' ? code : undefined,
      Array.isArray(missingFields)
        ? missingFields.filter((f): f is string => typeof f === 'string')
        : [],
    );
  }
  if (response.status === 204) return null as T;
  return response.json() as Promise<T>;
}

const lendingPlatformClient = {
  get: <T>(endpoint: string) => lendingFetch<T>(endpoint, { method: 'GET' }),
  put: <T>(endpoint: string, body: unknown) =>
    lendingFetch<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }),
};

export const lendingConfigApi = {
  /**
   * Get the saved lending configuration for the authenticated tenant.
   * Rejects with LENDING_CONFIGURATION_MISSING when the tenant has none.
   */
  get: async (): Promise<LendingConfiguration> => {
    const response = await lendingPlatformClient.get<LendingConfigResponse>(
      '/v1/lending-config',
    );
    return response.config;
  },

  /**
   * Update lending configuration for the authenticated tenant.
   * Accepts partial updates -- only provided fields are overwritten. A
   * tenant's first save carries every field. Over a saved configuration that
   * lacks fields, the update's haircuts replace the saved ones whole, so the
   * page's full-form save repairs it.
   */
  update: async (
    config: Partial<LendingConfiguration>,
  ): Promise<LendingConfiguration> => {
    const response = await lendingPlatformClient.put<LendingConfigResponse>(
      '/v1/lending-config',
      config,
    );
    return response.config;
  },

  /**
   * Get the hardcoded default lending configuration values.
   * Useful for the "Load Defaults" action in the admin UI.
   */
  getDefaults: async (): Promise<LendingConfiguration> => {
    const response = await lendingPlatformClient.get<LendingConfigResponse>(
      '/v1/lending-config/defaults',
    );
    return response.config;
  },
};
